import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import '@fontsource/convergence/latin-400.css';
import '@fontsource-variable/dm-sans/index.css';
import './styles/tokens.css';
import './index.css';
import { Provider } from 'react-redux';
import { store } from './store/index.ts';
import { configureConsole } from './utils/configureConsole.ts';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import * as Sentry from '@sentry/react';
import { bootstrapAuth } from './auth/bootstrap';
import { SENTRY_ENVIRONMENT } from './channel';
import AppErrorBoundary from './components/AppErrorBoundary';

Sentry.init({
  dsn: 'https://e7861215a108b13c6a0979a44962a7ac@o4506860334743552.ingest.us.sentry.io/4509050271891456',
  integrations: [Sentry.browserTracingIntegration()],
  // `production` is the built bundle at app.dooglie.com, `development` the
  // hosted dev channel at dev.dooglie.com (`--mode dev-hosted`), and `local`
  // somebody's dev server. Chosen by build mode, not `import.meta.env.PROD`:
  // PROD is true for every `vite build`, the dev-hosted one included, so it
  // would file dev.dooglie.com's errors under `production`.
  environment: SENTRY_ENVIRONMENT,

  tracesSampleRate: 1.0,
});

configureConsole();

/**
 * Longest the app waits for a session before showing itself anyway.
 *
 * Renewal is a round trip to Cognito, and a request that hangs used to hang the
 * whole app with it: nothing rendered, on any route, under a URL that looked
 * exactly right. Coming up signed out is recoverable — the login screen and the
 * gates tell the user what to do — whereas a blank page tells them nothing.
 */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 5000;

async function start() {
  await Promise.race([
    bootstrapAuth(),
    new Promise((resolve) =>
      window.setTimeout(resolve, AUTH_BOOTSTRAP_TIMEOUT_MS)
    ),
  ]);

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </ThemeProvider>
      </Provider>
    </React.StrictMode>
  );
}

void start();
